import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './auth/components/Login';
import Register from './features/author/components/Register';
import VerifyAccount from './auth/components/VerifyAccount';
import ErrorBoundary from './features/sharedComponents/errorBoundary';
import ResourceLibrary from './auth/components/ResourceLibrary';
import GetStarted from './auth/components/GetStarted';
import AccountRecovery from './auth/components/AccountRecovery';
import ChangePassword from './auth/components/ChangePassword';
import Layout from './features/admin/layout/Layout';
import Dashboard from './features/admin/pages/Dashboard';
import Submissions from './features/admin/pages/Submission';
import Reviews from './features/admin/pages/reviewPage';
import AuthorLayout from './features/author/layout/AuthorLayout';
import DashboardAuthor from './features/author/components/Dashboard';
import SubmissionsAuthor from './features/author/components/Submissions';
import CreateManuscript from './features/author/components/CreateManuscript';
import ApplicationForJournalSupport from './features/author/components/ApplicationForJournalSupport';
import { useAuth } from "./auth/hooks/auth";
import { AuthProvider } from './features/sharedComponents/AuthProvider';
import ManageMenu from './features/admin/pages/ManageMenu';
import EventCycle from './features/admin/pages/EventCycle';
import AuditLogs from './features/admin/pages/AuditLogs';
import ManageUser from './features/admin/pages/ManageUser';
import ManageCategory from './features/admin/pages/ManageCategory';
import Profile from './features/sharedComponents/Profile';
import ProfileCompletionModal from './features/sharedComponents/ProfileCompletionModal';
import BankDetailsCompletionModal from './features/sharedComponents/BankDetailsCompletionModal';
import ManuscriptReviewList from './features/admin/pages/manuscriptReviewList';
import ReviewEvaluation from './features/admin/pages/reviewEvaluation';
import ReviewerAssignment from './features/admin/pages/ReviewerAssignment';
import CollectiveAssessment from './features/admin/pages/CollectiveAssessment';
import RouteGuard from './features/sharedComponents/RouteGuard';
import AuthorCommunications from './features/admin/pages/AuthorCommunications';
import ReviewDecisionPage from './features/admin/pages/ReviewDecision';
import UserManual from './features/sharedComponents/UserManual';
import ModuleGuide from './features/sharedComponents/ModuleGuide';
import UserNotifications from './features/sharedComponents/UserNotifications';
import ApproveSubmissions from './features/admin/pages/ApproveSubmissions';
import FinalAssessment from './features/admin/pages/FinalAssessment';
import ReviewForm from './features/admin/pages/ReviewForm';
import PrimaFaciePage from './features/admin/pages/PrimaFaciePage'
import SubmissionsByDesk from './features/admin/pages/SubmissionsByDesk';
import AssignedSubmissionsForPrimaFacie from './features/admin/pages/AssignedSubmissionsForPrimaFacie';
import PrimaFacieReports from './features/admin/pages/PrimaFacieReports';
import InternalCommunications from './features/admin/pages/InternalCommunications';
import AssignedSubmissionsForReviewerEvaluation from './features/admin/pages/AssignedSubmissionsForReviewerEvaluation';
import ReviewerEvaluationReports from './features/admin/pages/ReviewerEvaluationReports';
import ReviewerEvaluationForm from './features/admin/pages/ReviewerEvaluationForm';
import TawkChat from './features/sharedComponents/TawkChat';
import ReviewedManuscriptsByDesk from './features/admin/pages/ReviewedManuscriptsByDesk';
import SearchSubmission from './features/admin/pages/SearchSubmission';
import SearchReviewedManuscript from './features/admin/pages/SearchReviewedManuscript';
import ManageInstitution from './features/admin/pages/ManageInstitution';
import JournalImpactAssessmentForm from './features/admin/pages/JournalImpactAssessmentForm';
import JournalsByDesk from './features/admin/pages/JournalsByDesk';
import AssignedSubmissionsForJournalAssessment from './features/admin/pages/AssignedSubmissionsForJournal';
import ReviewedManuscriptsForReReview from './features/admin/pages/ReviewedManuscriptsForReReview';
import JournalAssessmentReports from './features/admin/pages/JournalAssessmentReports';
import RevisedSubmissionsByDesk from './features/admin/pages/RevisedSubmissionsByDesk';
import ReviewerPayment from './features/admin/pages/ReviewerPayment';
import AssignedRevisionAssessments from './features/admin/pages/AssignedRevisionAssessments';
import RevisionAssessmentReports from './features/admin/pages/RevisionAssessmentReports';
import JournalApplicationEvaluationForm from './features/admin/pages/JournalApplicationEvaluationForm';
import AllSubmissions from './features/admin/pages/AllSubmissions';
import AllRevisedSubmissions from './features/admin/pages/AllRevisedSubmissions';
import AllReviewedManuscripts from './features/admin/pages/AllReviewedManuscripts';
import ApprovalPage from './features/admin/pages/ApprovalPage';
import ApprovalRequests from './features/admin/pages/ApprovalRequests';
import GenerateReport from './features/admin/pages/GenerateReport';
import ViewBatch from './features/admin/pages/ViewBatch';
import UpdateBatch from './features/admin/pages/UpdateBatch';
import ApprovalView from './features/admin/pages/ApprovalView';

const location = window.location;

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/admin" />;
};

const AdminRoutes: React.FC = () => (
  <Layout>
    <Routes>
      {/* Static admin routes */}
      <Route path="/" element={<Dashboard />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/user-guide" element={<UserManual />} />
      <Route path="/guide" element={<ModuleGuide />} />
      <Route path="/notifications" element={<UserNotifications />} />
      <Route path="/review-evaluation/*" element={<ReviewEvaluation />} />
      {/* <Route path="/journal-form" element={<JournalImpactAssessmentForm />} /> */}
      <Route path="/review-form/*" element={<ReviewForm />} />
      <Route path="/prima-facie/:submissionId" element={<PrimaFaciePage />} />
      <Route path="/reviewer-evaluation/:assignmentId" element={<ReviewerEvaluationForm />} />
      {/* <Route path="/journal-assessment/:assignmentId" element={<JournalImpactAssessmentForm />} /> */}
      <Route path="/journal-assessment/:assignmentId" element={<JournalApplicationEvaluationForm />} />

      {/* Menu-protected admin routes */}
      <Route element={<RouteGuard />}>
        <Route path="/submissions-desk" element={<SubmissionsByDesk />} />
        <Route path="/submissions" element={<Submissions />} />
        <Route path="/setup-menu" element={<ManageMenu />} />
        <Route path="/reviewers" element={<Reviews />} />
        <Route path="/event-cycle" element={<EventCycle />} />
        <Route path="/audit-logs" element={<AuditLogs />} />
        <Route path="/manage-users" element={<ManageUser />} />
        <Route path="/thematic-areas" element={<ManageCategory />} />
        <Route path="/manuscript-reviews" element={<ManuscriptReviewList />} />
        <Route path="/reviewer-assignment" element={<ReviewerAssignment />} />
        <Route path="/review-decision" element={<ReviewDecisionPage />} />
        <Route path="/collective-assessment" element={<CollectiveAssessment />} />
        <Route path="/final-assessment" element={<FinalAssessment />} />
        <Route path="/author-communications" element={<AuthorCommunications />} />
        <Route path="/internal-communications" element={<InternalCommunications />} />
        <Route path="/tetfund-approval" element={<ApproveSubmissions />} />
        <Route path="/prima-facie-assignments" element={<AssignedSubmissionsForPrimaFacie />} />
        <Route path="/prima-facie-reports" element={<PrimaFacieReports />} />
        <Route path="/reviewer-evaluation-assignments" element={<AssignedSubmissionsForReviewerEvaluation />} />
        <Route path="/reviewer-evaluation-reports" element={<ReviewerEvaluationReports />} />
        <Route path="/reviewed-manuscripts-desk" element={<ReviewedManuscriptsByDesk />} />
        <Route path="/search-submission" element={<SearchSubmission />} />
        <Route path="/search-reviewed-manuscript" element={<SearchReviewedManuscript />} />
        <Route path="/manage-institution" element={<ManageInstitution />} />
        <Route path="/journals-desk" element={<JournalsByDesk />} />
        <Route path="/journal-assessment-reports" element={<JournalAssessmentReports />} />
        <Route path="/journal-assessment-assignments" element={<AssignedSubmissionsForJournalAssessment />} />
        <Route path="/reviewed-manuscripts-for-rereview" element={<ReviewedManuscriptsForReReview />} />
        <Route path="/revised-submissions" element={<RevisedSubmissionsByDesk/>} />
        <Route path="/revision-assessments" element={<AssignedRevisionAssessments />} />
        <Route path="/revision-assessment-reports" element={<RevisionAssessmentReports />} />
        <Route path="/all-submissions" element={<AllSubmissions />} />
        <Route path="/all-revised-submissions" element={<AllRevisedSubmissions />} />
        <Route path="/all-reviewed-manuscripts" element={<AllReviewedManuscripts />} />
        <Route path="/reviewer-payments" element={<ReviewerPayment />} />
        <Route path="/approvals" element={<ApprovalPage />} />
        <Route path="/approvals/report/:id" element={<ApprovalView />} />
        <Route path="/approval-requests" element={<ApprovalRequests />} />
        <Route path="/approval-requests/generate" element={<GenerateReport />} />
        <Route path="/approval-requests/report/:id" element={<ViewBatch />} />
        <Route path="/approval-requests/:id/update" element={<UpdateBatch />} />
      </Route>

      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  </Layout>
);




const AuthorRoutes: React.FC = () => {
  const { user } = useAuth();
  const isJournalRep = user?.roleId === 13;

  return (
    <AuthorLayout>
      <Routes>
        {/* Author routes - static, no menu check needed as authors have predefined menus */}
        <Route path="/" element={<DashboardAuthor />} />
        <Route
          path="/create-manuscript"
          element={isJournalRep ? <Navigate to="/author" replace /> : <CreateManuscript />}
        />
        <Route
          path="/application-for-journal-support"
          element={isJournalRep ? <ApplicationForJournalSupport /> : <Navigate to="/author" replace />}
        />
        <Route path="/submissions" element={<SubmissionsAuthor />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/user-guide" element={<UserManual />} />
        <Route path="/guide" element={<ModuleGuide />} />
        <Route path="/notifications" element={<UserNotifications />} />
        <Route path="/library" element={<ResourceLibrary />} />
        <Route path="*" element={<Navigate to="/author" replace />} />
      </Routes>
    </AuthorLayout>
  );
};


const AppRoutes: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <>
      <ProfileCompletionModal isOpen={isAuthenticated && user?.profileCompleted === false} />
      <BankDetailsCompletionModal />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/library" element={<ResourceLibrary />} />
        <Route path="/get-started" element={<GetStarted />} />
        <Route path="/signup" element={<Register />} />
        <Route path="/verify-account" element={<VerifyAccount />} />
        <Route path="/reset-password" element={<AccountRecovery />} />

        {isAuthenticated ? (
          <>
            {user?.role !== 'author' ? (
              <Route path="/admin/*" element={
                <ProtectedRoute>
                  <AdminRoutes />
                </ProtectedRoute>
              } />
            ) : (
              <Route path="/author/*" element={
                <ProtectedRoute>
                  <AuthorRoutes />
                </ProtectedRoute>
              } />
            )}

            <Route path="/" element={
              <Navigate to={user?.role !== 'author' ? '/admin' : '/author'} replace />
            } />
          </>
        ) : (
          <Route path="/" element={<Navigate to="/login" replace />} />
        )}

        {/* Public Library Route removed and moved to AuthorRoutes */}

        <Route path="*" element={
          !isAuthenticated 
            ? <Navigate to="/login" replace /> 
            : <ErrorBoundary />
        } />

      </Routes>
    </>
  );
};

function App() {
  return (
    <Router>
      <AuthProvider>
        <TawkChat />
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
}

export default App;